/**
 * ReadOnlyAdminPage - Page d'administration en LECTURE SEULE
 * URL obscure: /admin-readonly-7k3m9x
 * Protégée par ADMIN_SECRET_CODE
 * 
 * Affiche pour chaque code bêta :
 * - Nombre de documents par collection
 * - Date premier/dernier document
 * - Date dernière activité
 * Trié par dernière activité décroissante
 */
import React, { useState, useEffect } from 'react';
import { Eye, RefreshCw, Shield, Calendar, Database, Clock, User, ChevronDown, ChevronUp } from 'lucide-react';

const ReadOnlyAdminPage = () => {
  const [adminCode, setAdminCode] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [stats, setStats] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Check localStorage for admin code
  useEffect(() => {
    const stored = localStorage.getItem('adminCode');
    if (stored) {
      setAdminCode(stored);
      // Auto-authenticate if code is stored
      validateAndFetch(stored);
    }
  }, []);

  const validateAndFetch = async (code) => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/beta/admin/readonly-stats`, {
        headers: { 'X-Admin-Code': code.toUpperCase() }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data.stats || []);
        setCollections(data.collections_scanned || []);
        setAuthenticated(true);
        localStorage.setItem('adminCode', code.toUpperCase());
      } else {
        setError('Code admin invalide');
        setAuthenticated(false);
      }
    } catch (err) {
      setError('Erreur de connexion');
      setAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    await validateAndFetch(adminCode);
  };

  const refreshStats = () => {
    if (adminCode) {
      validateAndFetch(adminCode);
    }
  };

  const toggleRow = (code) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(code)) {
      newExpanded.delete(code);
    } else {
      newExpanded.add(code);
    }
    setExpandedRows(newExpanded);
  };

  const formatDate = (isoString) => {
    if (!isoString) return '—';
    try {
      const date = new Date(isoString);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return isoString;
    }
  };

  const formatRelativeTime = (isoString) => {
    if (!isoString) return '—';
    try {
      const date = new Date(isoString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 60) return `il y a ${diffMins} min`;
      if (diffHours < 24) return `il y a ${diffHours}h`;
      if (diffDays < 7) return `il y a ${diffDays}j`;
      return formatDate(isoString);
    } catch {
      return isoString;
    }
  };

  // Login screen
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800 rounded-2xl p-8 w-full max-w-md shadow-xl border border-slate-700">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
              <Eye className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Stats Lecture Seule</h1>
              <p className="text-slate-400 text-sm">Administration ThaiPractice</p>
            </div>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-slate-300 text-sm mb-2">Code Admin</label>
              <input
                type="password"
                value={adminCode}
                onChange={(e) => setAdminCode(e.target.value)}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:border-emerald-500"
                placeholder="ADMIN-XXXXXXX"
                autoComplete="off"
              />
            </div>

            {error && (
              <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-3 text-red-300 text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !adminCode}
              className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-600 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Shield className="w-4 h-4" />
              )}
              {loading ? 'Chargement...' : 'Accéder'}
            </button>
          </form>

          <p className="text-slate-500 text-xs text-center mt-6">
            🔒 Accès lecture seule • Aucune modification possible
          </p>
        </div>
      </div>
    );
  }

  // Main stats view
  return (
    <div className="min-h-screen bg-slate-900 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
              <Eye className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Stats par Utilisateur</h1>
              <p className="text-slate-400 text-sm">
                {stats.length} codes • {collections.length} collections • Lecture seule
              </p>
            </div>
          </div>

          <button
            onClick={refreshStats}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-xl transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </button>
        </div>

        {/* Legend */}
        <div className="bg-slate-800/50 rounded-xl p-4 mb-6 border border-slate-700">
          <p className="text-slate-300 text-sm mb-2 font-medium">Collections scannées :</p>
          <div className="flex flex-wrap gap-2">
            {collections.map((coll) => (
              <span
                key={coll}
                className="px-2 py-1 bg-slate-700 text-slate-300 text-xs rounded-lg font-mono"
              >
                {coll}
              </span>
            ))}
          </div>
        </div>

        {/* Stats Table */}
        <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-slate-700/50 text-slate-300 text-sm font-medium border-b border-slate-700">
            <div className="col-span-3">Code / Label</div>
            <div className="col-span-2 text-center">Total Docs</div>
            <div className="col-span-3 text-center">Première activité</div>
            <div className="col-span-3 text-center">Dernière activité</div>
            <div className="col-span-1 text-center">Détail</div>
          </div>

          {/* Table Body */}
          {stats.length === 0 ? (
            <div className="px-4 py-12 text-center text-slate-400">
              {loading ? 'Chargement...' : 'Aucune donnée disponible'}
            </div>
          ) : (
            stats.map((item) => (
              <div key={item.code} className="border-b border-slate-700/50 last:border-b-0">
                {/* Main Row */}
                <div
                  className={`grid grid-cols-12 gap-2 px-4 py-3 hover:bg-slate-700/30 transition-colors cursor-pointer ${
                    item.revoked ? 'opacity-50' : ''
                  }`}
                  onClick={() => toggleRow(item.code)}
                >
                  <div className="col-span-3 flex items-center gap-2">
                    <User className="w-4 h-4 text-slate-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-white font-mono text-sm truncate" title={item.code}>
                        {item.code}
                        {item.revoked && (
                          <span className="ml-2 text-red-400 text-xs">(révoqué)</span>
                        )}
                      </p>
                      {item.label && (
                        <p className="text-slate-400 text-xs truncate">{item.label}</p>
                      )}
                    </div>
                  </div>

                  <div className="col-span-2 flex items-center justify-center">
                    <span
                      className={`px-3 py-1 rounded-lg text-sm font-medium ${
                        item.total_docs === 0
                          ? 'bg-slate-700 text-slate-400'
                          : item.total_docs < 50
                          ? 'bg-blue-500/20 text-blue-300'
                          : item.total_docs < 200
                          ? 'bg-emerald-500/20 text-emerald-300'
                          : 'bg-amber-500/20 text-amber-300'
                      }`}
                    >
                      <Database className="w-3 h-3 inline mr-1" />
                      {item.total_docs}
                    </span>
                  </div>

                  <div className="col-span-3 flex items-center justify-center text-slate-300 text-sm">
                    <Calendar className="w-3 h-3 mr-1 text-slate-500" />
                    {formatDate(item.first_activity)}
                  </div>

                  <div className="col-span-3 flex items-center justify-center">
                    <span
                      className={`text-sm ${
                        item.last_activity ? 'text-emerald-300' : 'text-slate-500'
                      }`}
                    >
                      <Clock className="w-3 h-3 inline mr-1" />
                      {formatRelativeTime(item.last_activity)}
                    </span>
                  </div>

                  <div className="col-span-1 flex items-center justify-center">
                    {expandedRows.has(item.code) ? (
                      <ChevronUp className="w-4 h-4 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    )}
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedRows.has(item.code) && (
                  <div className="px-4 py-3 bg-slate-800/50 border-t border-slate-700/50">
                    <p className="text-slate-400 text-xs mb-2 font-medium">
                      Documents par collection :
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                      {collections.map((coll) => {
                        const count = item.collections[coll] || 0;
                        return (
                          <div
                            key={coll}
                            className={`px-3 py-2 rounded-lg text-center ${
                              count === 0
                                ? 'bg-slate-700/50 text-slate-500'
                                : 'bg-slate-700 text-slate-200'
                            }`}
                          >
                            <p className="text-xs text-slate-400 truncate" title={coll}>
                              {coll.replace(/_/g, ' ')}
                            </p>
                            <p className="text-lg font-bold">{count}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <p className="text-slate-500 text-xs text-center mt-6">
          🔒 Mode lecture seule • Aucune requête d'écriture • Données en temps réel
        </p>
      </div>
    </div>
  );
};

export default ReadOnlyAdminPage;
